import React, { useState, useEffect } from "react";
import {
  Eye,
  Trash,
  FolderOpen,
  ArrowLeft,
  Search,
  Upload,
  RefreshCw,
  Grid,
  List,
} from "lucide-react";

const EnhancedMediaManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [gridView, setGridView] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentFolder, setCurrentFolder] = useState("");
  const [folderStructure, setFolderStructure] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Folders to navigate
  const mainFolders = [
    { name: "Root", path: "" },
    { name: "Blog", path: "blog" },
    { name: "Categories", path: "categories" },
    { name: "Config", path: "config" },
    { name: "Page", path: "page" },
    { name: "Page Videos", path: "page-videos" },
    { name: "Parent Categories", path: "parentCategories" },
    { name: "Products", path: "products" },
    { name: "Tickets", path: "tickets" },
  ];

  // Browse direct paths instead of using a special API endpoint
  useEffect(() => {
    if (currentFolder !== "") {
      fetchImages();
    }
  }, [page, currentFolder, searchQuery]);

  // Function to get direct image content instead of fetching folder structure
  const getDirectImageContent = async () => {
    try {
      const uploadFolders = mainFolders
        .map((folder) => folder.path)
        .filter((path) => path !== "");

      // Start with main uploads
      let allImages = [];

      for (const folder of uploadFolders) {
        try {
          setLoading(true);
          const token = localStorage.getItem("token");

          // Use a direct approach to just request images from each folder
          const response = await fetch(
            `/api/admin/uploads?folderPath=${folder}&limit=100`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.images && data.images.length > 0) {
              // Add folder info to each image
              const folderImages = data.images.map((img) => ({
                ...img,
                folder: folder,
              }));
              allImages = [...allImages, ...folderImages];
            }
          }
        } catch (error) {
          console.warn(`Error fetching images from ${folder}:`, error);
          // Continue with other folders
        }
      }

      setImages(allImages);
      setLoading(false);
    } catch (error) {
      console.error("Error getting direct image content:", error);
      setError("Failed to load images. Please try again.");
      setLoading(false);
    }
  };

  // Use this alternative approach on initial load
  useEffect(() => {
    if (currentFolder === "") {
      getDirectImageContent();
    }
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");

      // For direct file access, use the improved path
      let url;
      if (currentFolder) {
        // For specific folder, use the right endpoint
        url = `/api/admin/uploads?page=${page}&limit=24&folderPath=${encodeURIComponent(
          currentFolder
        )}`;
        if (searchQuery) {
          url += `&search=${encodeURIComponent(searchQuery)}`;
        }
      } else {
        // For root, list files directly from the upload folder
        url = `/api/admin/uploads?page=${page}&limit=24`;
        if (searchQuery) {
          url += `&search=${encodeURIComponent(searchQuery)}`;
        }
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch images");
      }

      const data = await response.json();

      // Process the data to ensure image objects have proper structure
      const processedImages = (data.images || []).map((img) => {
        // Ensure each image has a proper path
        if (!img.path && img.originalName) {
          img.path = img.originalName;
        }
        return img;
      });

      setImages(processedImages);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching images:", error);
      setError("Failed to load images. Please try again.");

      // As a fallback, try getting the images directly
      if (currentFolder) {
        try {
          const token = localStorage.getItem("token");
          const fallbackUrl = `/api/upload/${currentFolder}`;

          // This is a best-effort attempt to get directory listing
          const fallbackResponse = await fetch(fallbackUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });

          // If successful, extract image URLs from HTML response
          if (fallbackResponse.ok) {
            const html = await fallbackResponse.text();
            // Extract image URLs from directory listing HTML (simplified approach)
            const imgMatches = html.match(
              /<a href="([^"]+\.(jpg|jpeg|png|gif|mp4|webp))"/gi
            );

            if (imgMatches && imgMatches.length > 0) {
              const extractedImages = imgMatches.map((match, index) => {
                const filename = match
                  .replace(/<a href="/i, "")
                  .replace(/".*$/i, "");
                return {
                  _id: `fallback-${index}`,
                  path: `${currentFolder}/${filename}`,
                  originalName: filename,
                  contentType: getContentTypeFromExt(filename),
                };
              });

              setImages(extractedImages);
            }
          }
        } catch (fallbackError) {
          console.error("Fallback directory listing failed:", fallbackError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to guess content type from extension
  const getContentTypeFromExt = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    const typeMap = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      mp4: "video/mp4",
      webm: "video/webm",
      mov: "video/quicktime",
    };
    return typeMap[ext] || "application/octet-stream";
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const token = localStorage.getItem("token");
      // Using existing endpoint from server.js: /api/admin/uploads/:id
      const response = await fetch(`/api/admin/uploads/${imageId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete image");
      }

      // Remove the deleted image from the array
      setImages(images.filter((img) => img._id !== imageId));

      // Close the preview if the deleted image was selected
      if (selectedImage && selectedImage._id === imageId) {
        setSelectedImage(null);
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      alert(error.message || "Failed to delete image");
    }
  };

  const navigateToFolder = (folderPath) => {
    setCurrentFolder(folderPath);
    setPage(1); // Reset to first page when changing folders
  };

  const handleUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();

    // Add folder information if available
    if (currentFolder) {
      // For page uploads, we need to send pageSlug
      if (currentFolder.startsWith("page/")) {
        const pageSlug = currentFolder.replace("page/", "");
        formData.append("pageSlug", pageSlug);
      } else {
        formData.append("folder", currentFolder);
      }
    }

    // Using the first file to determine upload type
    const file = files[0];
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    // Determine the correct field name and endpoint based on file type and folder
    let endpoint = "/api/upload-image";
    let fieldName = "image";

    if (isVideo && currentFolder.startsWith("page")) {
      endpoint = "/api/upload-page-video";
      fieldName = "video";
    } else if (currentFolder.startsWith("page")) {
      endpoint = "/api/upload-page-image";
    }

    // Add all files to the form
    for (let i = 0; i < files.length; i++) {
      formData.append(fieldName, files[i]);
    }

    try {
      const token = localStorage.getItem("token");
      const xhr = new XMLHttpRequest();

      xhr.open("POST", endpoint, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round(
            (event.loaded * 100) / event.total
          );
          setUploadProgress(percentCompleted);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          // Refresh image list after upload
          fetchImages();
        } else {
          console.error("Upload failed with status:", xhr.status);
          alert("Failed to upload files");
        }
        setUploading(false);
      };

      xhr.onerror = () => {
        console.error("Error uploading files");
        alert("Failed to upload files");
        setUploading(false);
      };

      xhr.send(formData);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert(error.message || "Failed to upload files");
      setUploading(false);
    }
  };

  const getImageUrl = (image) => {
    // Extract filename - this is what we need to focus on
    const filename = getFilename(image.path || image.originalName || "");

    if (!filename) {
      return "/api/upload/placeholder.jpg"; // Fallback
    }

    // Handle different types of image paths
    if (image.path) {
      // Direct handling of image paths from server
      if (image.path.startsWith("/upload/")) {
        return `/api${image.path}`;
      } else if (image.path.startsWith("/api/upload/")) {
        return image.path;
      } else if (image.path.startsWith("upload/")) {
        return `/api/${image.path}`;
      }

      // If it's just a filename, construct the URL based on the folder
      if (!image.path.includes("/")) {
        const folder = image.folder || currentFolder || "";
        if (folder) {
          return `/api/upload/${folder}/${filename}`;
        } else {
          return `/api/upload/${filename}`;
        }
      }

      // Default case - try to use the path as is
      return `/api/upload/${filename}`;
    } else if (image.data) {
      // For images stored as base64 data
      return `data:${image.contentType || "image/jpeg"};base64,${image.data}`;
    } else if (image.url) {
      // For images with direct URLs
      return image.url;
    }

    // Last resort fallback - try to construct URL from filename
    return `/api/upload/${filename}`;
  };

  // Format file size to human-readable format
  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Extract filename from path more robustly
  const getFilename = (path) => {
    if (!path) return "Unknown";

    // For timestamps with underscore (common in your system)
    if (/^\d+_/.test(path)) {
      return path;
    }

    // Try different path formats
    let filename;

    if (path.includes("/")) {
      // Regular path with slashes
      filename = path.split("/").pop();
    } else if (path.includes("\\")) {
      // Windows-style path
      filename = path.split("\\").pop();
    } else {
      // Already a filename
      filename = path;
    }

    // Remove any query parameters if present
    if (filename.includes("?")) {
      filename = filename.split("?")[0];
    }

    return filename;
  };

  // Determine folder navigation breadcrumbs
  const generateBreadcrumbs = () => {
    if (!currentFolder) {
      return [{ name: "Root", path: "" }];
    }

    const parts = currentFolder.split("/");
    let currentPath = "";

    return [
      { name: "Root", path: "" },
      ...parts.map((part) => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        return { name: part, path: currentPath };
      }),
    ];
  };

  const breadcrumbs = generateBreadcrumbs();

  if (loading && !images.length) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-500" />
        <p className="mt-4 text-gray-600">Loading media files...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with title and view toggles */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Media Manager</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setGridView(true)}
            className={`p-2 rounded ${
              gridView ? "bg-blue-500 text-white" : "bg-gray-200"
            }`}
            title="Grid View"
          >
            <Grid size={18} />
          </button>
          <button
            onClick={() => setGridView(false)}
            className={`p-2 rounded ${
              !gridView ? "bg-blue-500 text-white" : "bg-gray-200"
            }`}
            title="List View"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Search, folder navigation, and upload */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        {/* Search */}
        <div className="relative flex-grow max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search files by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Upload Button */}
        <label className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md flex items-center gap-2 transition-colors">
          <Upload size={18} />
          Upload Files
          <input
            type="file"
            multiple
            className="hidden"
            onChange={handleUpload}
            accept="image/*"
          />
        </label>

        {/* Refresh Button */}
        <button
          onClick={fetchImages}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
          <div
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          ></div>
          <p className="text-sm text-gray-500 mt-1">
            Uploading: {uploadProgress}%
          </p>
        </div>
      )}

      {/* Breadcrumb Navigation */}
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          {breadcrumbs.map((crumb, index) => (
            <li key={index} className="inline-flex items-center">
              {index > 0 && <span className="mx-2 text-gray-400">/</span>}
              <button
                onClick={() => navigateToFolder(crumb.path)}
                className={`inline-flex items-center text-sm ${
                  index === breadcrumbs.length - 1
                    ? "font-medium text-blue-600"
                    : "text-gray-700 hover:text-blue-600"
                }`}
              >
                {index === 0 && <FolderOpen className="mr-1 h-4 w-4" />}
                {crumb.name}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      {/* Folder Navigation */}
      {currentFolder === "" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
          {mainFolders.map((folder) => (
            <button
              key={folder.path}
              onClick={() => navigateToFolder(folder.path)}
              className="flex flex-col items-center justify-center p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FolderOpen className="h-12 w-12 text-blue-500 mb-2" />
              <span className="text-sm font-medium">{folder.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="text-center text-red-600 p-4">
          {error}
          <button
            onClick={fetchImages}
            className="ml-2 text-blue-600 hover:text-blue-800"
          >
            Retry
          </button>
        </div>
      )}

      {/* Media display area */}
      {images.length === 0 && !loading ? (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No images found in this folder.</p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="mt-2 text-blue-500 hover:text-blue-700"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Grid View */}
          {gridView ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <div key={index} className="relative group">
                  {image.path && image.path.toLowerCase().endsWith(".mp4") ? (
                    // Video element for MP4 files
                    <div className="w-full h-48 flex items-center justify-center bg-black rounded-lg">
                      <video
                        className="h-full max-w-full object-contain"
                        controls
                        muted
                      >
                        <source src={getImageUrl(image)} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : (
                    // Image element for images
                    <img
                      src={getImageUrl(image)}
                      alt={
                        getFilename(image.path || image.originalName || "") ||
                        "Uploaded content"
                      }
                      className="w-full h-48 object-cover rounded-lg shadow-md"
                      onError={(e) => {
                        // Fall back to using just the filename if the URL fails
                        const filename = getFilename(
                          image.path || image.originalName || ""
                        );
                        e.target.src = `/api/upload/${filename}`;
                        e.target.onerror = () => {
                          // If that fails too, use a placeholder
                          e.target.src =
                            "https://via.placeholder.com/150?text=Image+Not+Found";
                          e.target.onerror = null;
                        };
                      }}
                    />
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                    <button
                      onClick={() => setSelectedImage(image)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100"
                      title="View"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(image._id)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 text-red-600"
                      title="Delete"
                    >
                      <Trash className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 truncate">
                    {getFilename(image.path || image.originalName || "")}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Preview
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Filename
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {images.map((image) => (
                    <tr key={image._id}>
                      <td className="px-6 py-4">
                        <img
                          src={getImageUrl(image)}
                          alt="Thumbnail"
                          className="h-16 w-16 object-cover rounded"
                        />
                      </td>
                      <td className="px-6 py-4 truncate max-w-xs">
                        {getFilename(image.path || "")}
                      </td>
                      <td className="px-6 py-4">
                        {image.contentType || "image"}
                      </td>
                      <td className="px-6 py-4">
                        {formatFileSize(image.size)}
                      </td>
                      <td className="px-6 py-4">
                        {new Date(image.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 space-x-3">
                        <button
                          onClick={() => setSelectedImage(image)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100 transition-colors"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(image._id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                          title="Delete"
                        >
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border rounded disabled:opacity-50 transition-opacity"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 border rounded disabled:opacity-50 transition-opacity"
          >
            Next
          </button>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full">
            <div className="relative p-2">
              <h3 className="text-lg font-medium text-center mb-2">
                {getFilename(selectedImage.path || "")}
              </h3>
              {selectedImage.path &&
              selectedImage.path.toLowerCase().endsWith(".mp4") ? (
                <video controls className="w-full h-auto max-h-[70vh]">
                  <source src={getImageUrl(selectedImage)} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={getImageUrl(selectedImage)}
                  alt="Full size"
                  className="w-full h-auto max-h-[70vh] object-contain"
                  onError={(e) => {
                    // Fall back to using just the filename
                    const filename = getFilename(
                      selectedImage.path || selectedImage.originalName || ""
                    );
                    e.target.src = `/api/upload/${filename}`;
                    e.target.onerror = () => {
                      // If that fails too, use a placeholder
                      e.target.src =
                        "https://via.placeholder.com/400x300?text=Image+Not+Found";
                      e.target.onerror = null;
                    };
                  }}
                />
              )}

              {/* Image Details */}
              <div className="mt-4 px-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p>
                    <strong>Type:</strong>{" "}
                    {selectedImage.contentType || "Unknown"}
                  </p>
                  <p>
                    <strong>Size:</strong> {formatFileSize(selectedImage.size)}
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Created:</strong>{" "}
                    {new Date(selectedImage.createdAt).toLocaleString()}
                  </p>
                  <p>
                    <strong>Path:</strong> {selectedImage.path || "Unknown"}
                  </p>
                </div>
              </div>

              {/* Copy URL button */}
              <div className="px-4 mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={getImageUrl(selectedImage)}
                    className="border rounded px-2 py-1 text-sm flex-grow"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(getImageUrl(selectedImage));
                      alert("URL copied to clipboard!");
                    }}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                  >
                    Copy URL
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end space-x-2">
              <button
                onClick={() => handleDelete(selectedImage._id)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedImage(null)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedMediaManager;

// // before having img in upload folder
// // src/pages/MediaManager.js
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { Eye, Trash } from 'lucide-react';

// const MediaManager = () => {
//   const [images, setImages] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedImage, setSelectedImage] = useState(null);
//   const [gridView, setGridView] = useState(true);
//   const [page, setPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   useEffect(() => {
//     fetchImages();
//   }, [page]);

//   const fetchImages = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const token = localStorage.getItem('token');
//       const response = await axios.get(`/api/images?page=${page}`, {
//         headers: { Authorization: `Bearer ${token}` }
//       });
//       setImages(response.data.images);
//       setTotalPages(response.data.totalPages);
//     } catch (error) {
//       console.error('Error fetching images:', error);
//       setError('Failed to load images. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDelete = async (imageId) => {
//     if (!window.confirm('Are you sure you want to delete this image?')) return;

//     try {
//       const token = localStorage.getItem('token');
//       await axios.delete(`/api/images/${imageId}`, {
//         headers: { Authorization: `Bearer ${token}` }
//       });
//       setImages(images.filter(img => img._id !== imageId));
//     } catch (error) {
//       console.error('Error deleting image:', error);
//       alert(error.response?.data?.error || 'Failed to delete image');
//     }
//   };

//   if (loading && !images.length) {
//     return (
//       <div className="fixed inset-0 flex flex-col items-center justify-center">
//         {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /> */}
//         <img src='../images/loading.gif'/>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="text-center text-red-600 p-4">
//         {error}
//         <button
//           onClick={fetchImages}
//           className="ml-2 text-blue-600 hover:text-blue-800"
//         >
//           Retry
//         </button>
//       </div>
//     );
//   }

//   return (
//     <div className="space-y-6">
//       <div className="flex justify-between items-center">
//         <h2 className="text-2xl font-semibold">Media Manager</h2>
//         <div className="flex gap-2">
//           <button
//             onClick={() => setGridView(true)}
//             className={`p-2 rounded ${gridView ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
//           >
//             Grid
//           </button>
//           <button
//             onClick={() => setGridView(false)}
//             className={`p-2 rounded ${!gridView ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
//           >
//             List
//           </button>
//         </div>
//       </div>

//       {gridView ? (
//         <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
//           {images.map((image) => (
//             <div key={image._id} className="relative group">
//               <img
//                 src={`data:${image.contentType};base64,${image.data}`}
//                 alt="Uploaded content"
//                 className="w-full h-48 object-cover rounded-lg shadow-md"
//               />
//               <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
//                 <button
//                   onClick={() => setSelectedImage(image)}
//                   className="p-2 bg-white rounded-full hover:bg-gray-100"
//                 >
//                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
//                   </svg>
//                 </button>
//                 <button
//                   onClick={() => handleDelete(image._id)}
//                   className="p-2 bg-white rounded-full hover:bg-gray-100 text-red-600"
//                 >
//                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
//                   </svg>
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>
//       ) : (
//         <div className="bg-white rounded-lg shadow overflow-hidden">
//           <table className="min-w-full divide-y divide-gray-200">
//             <thead className="bg-gray-50">
//               <tr>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preview</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-gray-200">
//               {images.map((image) => (
//                 <tr key={image._id}>
//                   <td className="px-6 py-4">
//                     <img
//                       src={`data:${image.contentType};base64,${image.data}`}
//                       alt="Thumbnail"
//                       className="h-16 w-16 object-cover rounded"
//                     />
//                   </td>
//                   <td className="px-6 py-4">{image.contentType}</td>
//                   <td className="px-6 py-4">
//                     {new Date(image.createdAt).toLocaleDateString()}
//                   </td>
//                   <td className="px-6 py-4 space-x-3">
//                     <button
//                       onClick={() => setSelectedImage(image)}
//                       className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100 transition-colors"
//                       title="View"
//                     >
//                       <Eye size={18} />
//                     </button>
//                     <button
//                       onClick={() => handleDelete(image._id)}
//                       className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
//                       title="Delete"
//                     >
//                       <Trash size={18} />
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {/* Pagination */}
//       {totalPages > 1 && (
//         <div className="flex justify-center space-x-2 mt-4">
//           <button
//             onClick={() => setPage(p => Math.max(1, p - 1))}
//             disabled={page === 1}
//             className="px-4 py-2 border rounded disabled:opacity-50"
//           >
//             Previous
//           </button>
//           <span className="px-4 py-2">
//             Page {page} of {totalPages}
//           </span>
//           <button
//             onClick={() => setPage(p => Math.min(totalPages, p + 1))}
//             disabled={page === totalPages}
//             className="px-4 py-2 border rounded disabled:opacity-50"
//           >
//             Next
//           </button>
//         </div>
//       )}

//       {/* Image Preview Modal */}
//       {selectedImage && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg max-w-2xl w-full">
//             <div className="p-4">
//               <img
//                 src={`data:${selectedImage.contentType};base64,${selectedImage.data}`}
//                 alt="Full size"
//                 className="w-full h-auto"
//               />
//             </div>
//             <div className="p-4 border-t flex justify-end">
//               <button
//                 onClick={() => setSelectedImage(null)}
//                 className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default MediaManager;
